import journeyCatalog from "@/data/languageJourneys.json";

export type LanguageJourneyCefrLevel = "A1" | "A2" | "B1" | "B2" | "C1";
export type LanguageJourneyCefrBand = LanguageJourneyCefrLevel | "A1-A2" | "A2-B1" | "B1-B2";

export type LanguageJourneyChapter = {
  id: string;
  legacyChapterIds?: string[];
  chapterNumber: number;
  unitNumber?: number;
  unitChapterNumber?: number;
  unitTitle?: string;
  title: string;
  cefrLevel: LanguageJourneyCefrLevel;
  cefrLevelTitle: string;
  cefrLevelChapterNumber: number;
  cefrCanDo: string;
  isFreePreview: boolean;
  objective: string;
  learnerDescription: string;
  focus: "grammar_word_order";
  coreForms: string;
  newConcept?: string;
  sampleChallenge: string;
  challengeGloss?: string;
};

export type LanguageJourneyGroup = {
  id: string;
  label: string;
  title: string;
  chapterStart: number;
  chapterEnd: number;
  freePreviewChapters: number;
  canDo: string;
  chapters: LanguageJourneyChapter[];
  cefrBand?: LanguageJourneyCefrBand;
};

export type LanguageJourney = {
  languageCode: string;
  alternateLanguageCodes?: string[];
  slug: string;
  languageName: string;
  nativeName: string;
  instructionLanguage: string;
  instructionLanguageCode?: string;
  sourceLabel?: string;
  sourceDescription?: string;
  sourceLogoSrc?: string;
  sourceLogoAlt?: string;
  releaseStage?: "pilot" | "complete";
  courseVersion?: number;
  contentRevision?: number;
  learnerCopyMode?: "authored";
  progressNamespace?: string;
  groupingMode?: "cefr_levels" | "course_units";
  courseUnits?: Array<{
    id: string;
    unitNumber: number;
    title: string;
    cefrBand: LanguageJourneyCefrBand;
    chapterStart: number;
    chapterEnd: number;
    freePreviewChapters: number;
    canDo: string;
  }>;
  cefrLevels: Array<{
    level: LanguageJourneyChapter["cefrLevel"];
    title: string;
    chapterStart: number;
    chapterEnd: number;
    freePreviewChapters: number;
    canDo: string;
  }>;
  chapters: LanguageJourneyChapter[];
};

type LanguageJourneyCatalog = {
  version: number;
  instructionLanguage: "English";
  minimumChaptersPerLanguage: number;
  freePreviewChaptersPerLevel: number;
  languages: LanguageJourney[];
};

const catalog = journeyCatalog as LanguageJourneyCatalog;
const PROGRESS_STORAGE_PREFIX = "chicky:language_journey_progress:";
const GUEST_PROGRESS_IDENTITY = "guest";
export const LANGUAGE_JOURNEY_MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;

export type LanguageJourneyChapterState = {
  completed: boolean;
  updatedAt: string;
};

export type LanguageJourneyProgress = {
  completedChapterIds: string[];
  lastChapterId: string | null;
  updatedAt: string | null;
  chapterStates?: Record<string, LanguageJourneyChapterState>;
};

const emptyProgress = (): LanguageJourneyProgress => ({
  completedChapterIds: [],
  lastChapterId: null,
  updatedAt: null,
  chapterStates: {},
});

export function getLanguageJourneyCatalog(): LanguageJourneyCatalog {
  return catalog;
}

export function getLanguageJourneyByCode(languageCode: string | null | undefined) {
  if (!languageCode) return null;
  return (
    catalog.languages.find((journey) => journey.languageCode === languageCode) ||
    catalog.languages.find((journey) => journey.alternateLanguageCodes?.includes(languageCode)) ||
    catalog.languages.find((journey) => journey.languageCode.split("-")[0] === languageCode) ||
    null
  );
}

export function getDefaultLanguageJourneyChapter(languageCode: string | null | undefined) {
  return getLanguageJourneyByCode(languageCode)?.chapters[0] || null;
}

export function getLanguageJourneyChapter(
  languageCode: string | null | undefined,
  chapterId: string | null | undefined
) {
  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey) return null;
  if (!chapterId) return journey.chapters[0] || null;
  return (
    journey.chapters.find((chapter) => chapter.id === chapterId) ||
    journey.chapters.find((chapter) => chapter.legacyChapterIds?.includes(chapterId)) ||
    journey.chapters.find((chapter) => String(chapter.chapterNumber) === chapterId) ||
    journey.chapters[0] ||
    null
  );
}

export function getLanguageJourneyChaptersByLevel(journey: LanguageJourney | null | undefined) {
  if (!journey) return [];

  return journey.cefrLevels.map((level) => ({
    ...level,
    chapters: journey.chapters.filter((chapter) => chapter.cefrLevel === level.level),
  }));
}

export function getLanguageJourneyChapterGroups(
  journey: LanguageJourney | null | undefined
): LanguageJourneyGroup[] {
  if (!journey) return [];

  if (journey.groupingMode === "course_units" && journey.courseUnits?.length) {
    const unitLabel = journey.instructionLanguageCode?.toLowerCase().startsWith("es")
      ? "Unidad"
      : "Unit";
    return journey.courseUnits.map((unit) => ({
      id: String(unit.unitNumber),
      label: `${unitLabel} ${unit.unitNumber}`,
      title: unit.title,
      chapterStart: unit.chapterStart,
      chapterEnd: unit.chapterEnd,
      freePreviewChapters: unit.freePreviewChapters,
      canDo: unit.canDo,
      cefrBand: unit.cefrBand,
      chapters: journey.chapters.filter((chapter) => chapter.unitNumber === unit.unitNumber),
    }));
  }

  return journey.cefrLevels.map((level) => ({
    id: level.level,
    label: level.level,
    title: level.title,
    chapterStart: level.chapterStart,
    chapterEnd: level.chapterEnd,
    freePreviewChapters: level.freePreviewChapters,
    canDo: level.canDo,
    chapters: journey.chapters.filter((chapter) => chapter.cefrLevel === level.level),
  }));
}

export function getLanguageJourneyChapterGroupId(
  journey: LanguageJourney | null | undefined,
  chapter: LanguageJourneyChapter | null | undefined
) {
  if (!journey || !chapter) return null;
  if (journey.groupingMode === "course_units" && chapter.unitNumber) {
    return String(chapter.unitNumber);
  }
  return chapter.cefrLevel;
}

export function getLanguageJourneyChapterDisplayDescription(
  journey: LanguageJourney | null | undefined,
  chapter: LanguageJourneyChapter | null | undefined
) {
  if (!chapter) return "";

  if (journey?.groupingMode === "course_units") {
    if (
      journey.learnerCopyMode === "authored" ||
      journey.instructionLanguageCode?.toLowerCase().startsWith("es")
    ) {
      return chapter.learnerDescription;
    }
    return chapter.sampleChallenge ? `Say: ${chapter.sampleChallenge}` : chapter.title;
  }

  return chapter.learnerDescription;
}

export function getLanguageJourneyChapterDisplayFocus(
  journey: LanguageJourney | null | undefined,
  chapter: LanguageJourneyChapter | null | undefined
) {
  if (!chapter) return "";

  if (journey?.groupingMode === "course_units") {
    if (
      journey.learnerCopyMode === "authored" ||
      journey.instructionLanguageCode?.toLowerCase().startsWith("es")
    ) {
      return journey.instructionLanguageCode?.toLowerCase().startsWith("es")
        ? `Practica: ${chapter.coreForms}`
        : `Practice: ${chapter.coreForms}`;
    }
    return chapter.newConcept ? `New: ${chapter.newConcept}` : `Focus: ${chapter.coreForms}`;
  }

  return `Focus: ${chapter.coreForms}`;
}

export function getLanguageJourneyChapterDisplayObjective(
  journey: LanguageJourney | null | undefined,
  chapter: LanguageJourneyChapter | null | undefined
) {
  if (!chapter) return "";

  if (journey?.groupingMode === "course_units") {
    if (
      journey.learnerCopyMode === "authored" ||
      journey.instructionLanguageCode?.toLowerCase().startsWith("es")
    ) {
      return chapter.objective;
    }
    return chapter.sampleChallenge ? `Practice saying: ${chapter.sampleChallenge}` : chapter.title;
  }

  return chapter.objective;
}

export function isLanguageJourneyChapterUnlocked(
  chapter: LanguageJourneyChapter | null | undefined,
  subscribed: boolean
) {
  if (!chapter) return false;
  return subscribed || chapter.isFreePreview;
}

export function getLanguageJourneyProgressIdentityScope(userId?: string | null) {
  return userId ? `user:${encodeURIComponent(userId)}` : GUEST_PROGRESS_IDENTITY;
}

export function getLanguageJourneyProgressNamespace(
  languageCode: string | null | undefined
): string | null {
  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey || !languageCode) return null;
  return journey.progressNamespace || journey.languageCode;
}

function getLegacyProgressStorageKey(languageCode: string, progressNamespace?: string | null) {
  return `${PROGRESS_STORAGE_PREFIX}${progressNamespace || languageCode}`;
}

function getProgressStorageKey(
  languageCode: string,
  progressNamespace?: string | null,
  userId?: string | null
) {
  return `${getLegacyProgressStorageKey(languageCode, progressNamespace)}:identity:${getLanguageJourneyProgressIdentityScope(userId)}`;
}

function parseIsoTimestamp(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  const now = Date.now();
  // A broken or modified client clock must not create a far-future LWW value
  // that permanently blocks later completion/undo changes.
  return new Date(
    timestamp > now + LANGUAGE_JOURNEY_MAX_FUTURE_SKEW_MS ? now : timestamp
  ).toISOString();
}

function chooseLastCompletedChapterId(
  journey: LanguageJourney,
  chapterStates: Record<string, LanguageJourneyChapterState>
) {
  let selected: { id: string; updatedAt: string; chapterNumber: number } | null = null;

  for (const chapter of journey.chapters) {
    const state = chapterStates[chapter.id];
    if (!state?.completed) continue;
    if (
      !selected ||
      state.updatedAt > selected.updatedAt ||
      (state.updatedAt === selected.updatedAt && chapter.chapterNumber > selected.chapterNumber)
    ) {
      selected = {
        id: chapter.id,
        updatedAt: state.updatedAt,
        chapterNumber: chapter.chapterNumber,
      };
    }
  }

  return selected?.id || null;
}

export function normalizeLanguageJourneyProgress(
  languageCode: string | null | undefined,
  progress: Partial<LanguageJourneyProgress> | null | undefined
): LanguageJourneyProgress {
  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey) return emptyProgress();

  const validChapterIds = new Set(journey.chapters.map((chapter) => chapter.id));
  const progressUpdatedAt = parseIsoTimestamp(progress?.updatedAt);
  const legacyUpdatedAt = progressUpdatedAt || "1970-01-01T00:00:00.000Z";
  const chapterStates: Record<string, LanguageJourneyChapterState> = {};

  if (progress?.chapterStates && typeof progress.chapterStates === "object") {
    for (const [chapterId, candidate] of Object.entries(progress.chapterStates)) {
      if (!validChapterIds.has(chapterId) || !candidate || typeof candidate !== "object") continue;
      const updatedAt = parseIsoTimestamp(candidate.updatedAt);
      if (typeof candidate.completed !== "boolean" || !updatedAt) continue;
      chapterStates[chapterId] = { completed: candidate.completed, updatedAt };
    }
  }

  if (Array.isArray(progress?.completedChapterIds)) {
    for (const chapterId of progress.completedChapterIds) {
      if (typeof chapterId !== "string" || !validChapterIds.has(chapterId)) continue;
      if (!chapterStates[chapterId]) {
        chapterStates[chapterId] = { completed: true, updatedAt: legacyUpdatedAt };
      }
    }
  }

  const completedChapterIds = journey.chapters
    .filter((chapter) => chapterStates[chapter.id]?.completed)
    .map((chapter) => chapter.id);
  const latestStateUpdatedAt = Object.values(chapterStates).reduce<string | null>(
    (latest, state) => (!latest || state.updatedAt > latest ? state.updatedAt : latest),
    null
  );
  const updatedAt =
    progressUpdatedAt && latestStateUpdatedAt
      ? progressUpdatedAt > latestStateUpdatedAt
        ? progressUpdatedAt
        : latestStateUpdatedAt
      : progressUpdatedAt || latestStateUpdatedAt;
  const requestedLastChapterId =
    typeof progress?.lastChapterId === "string" && chapterStates[progress.lastChapterId]?.completed
      ? progress.lastChapterId
      : null;

  return {
    completedChapterIds,
    lastChapterId: requestedLastChapterId || chooseLastCompletedChapterId(journey, chapterStates),
    updatedAt,
    chapterStates,
  };
}

export function mergeLanguageJourneyProgress(
  languageCode: string,
  first: Partial<LanguageJourneyProgress> | null | undefined,
  second: Partial<LanguageJourneyProgress> | null | undefined
) {
  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey) return emptyProgress();
  const normalizedFirst = normalizeLanguageJourneyProgress(languageCode, first);
  const normalizedSecond = normalizeLanguageJourneyProgress(languageCode, second);
  const chapterStates: Record<string, LanguageJourneyChapterState> = {
    ...(normalizedFirst.chapterStates || {}),
  };

  for (const [chapterId, candidate] of Object.entries(normalizedSecond.chapterStates || {})) {
    const current = chapterStates[chapterId];
    if (
      !current ||
      candidate.updatedAt > current.updatedAt ||
      (candidate.updatedAt === current.updatedAt && candidate.completed && !current.completed)
    ) {
      chapterStates[chapterId] = candidate;
    }
  }

  return normalizeLanguageJourneyProgress(languageCode, {
    completedChapterIds: [],
    lastChapterId: chooseLastCompletedChapterId(journey, chapterStates),
    updatedAt:
      normalizedFirst.updatedAt && normalizedSecond.updatedAt
        ? normalizedFirst.updatedAt > normalizedSecond.updatedAt
          ? normalizedFirst.updatedAt
          : normalizedSecond.updatedAt
        : normalizedFirst.updatedAt || normalizedSecond.updatedAt,
    chapterStates,
  });
}

function parseStoredProgress(languageCode: string, raw: string | null) {
  if (!raw) return null;
  try {
    return normalizeLanguageJourneyProgress(
      languageCode,
      JSON.parse(raw) as Partial<LanguageJourneyProgress>
    );
  } catch {
    return null;
  }
}

export function loadLanguageJourneyProgress(
  languageCode: string | null | undefined,
  userId?: string | null
): LanguageJourneyProgress {
  if (!languageCode || typeof window === "undefined") return emptyProgress();

  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey) return emptyProgress();

  try {
    const scopedKey = getProgressStorageKey(languageCode, journey.progressNamespace, userId);
    const scoped = parseStoredProgress(languageCode, window.localStorage.getItem(scopedKey));
    if (scoped) return scoped;

    // The old key was shared by every signed-in account. It is safe to migrate only
    // into the guest namespace; authenticated accounts start from their own scope.
    if (!userId) {
      const legacy = parseStoredProgress(
        languageCode,
        window.localStorage.getItem(
          getLegacyProgressStorageKey(languageCode, journey.progressNamespace)
        )
      );
      if (legacy) {
        window.localStorage.setItem(scopedKey, JSON.stringify(legacy));
        return legacy;
      }
    }
  } catch {
    // Local persistence is best effort; callers keep an empty/offline-safe state.
  }

  return emptyProgress();
}

export function saveLanguageJourneyProgress(
  languageCode: string,
  progress: LanguageJourneyProgress,
  userId?: string | null
) {
  const normalized = normalizeLanguageJourneyProgress(languageCode, progress);
  if (typeof window === "undefined") return normalized;
  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey) return normalized;
  const identityScope = getLanguageJourneyProgressIdentityScope(userId);
  window.localStorage.setItem(
    getProgressStorageKey(languageCode, journey.progressNamespace, userId),
    JSON.stringify(normalized)
  );
  window.dispatchEvent(
    new CustomEvent("language-journey-progress-updated", {
      detail: {
        languageCode: journey.languageCode,
        progressNamespace: getLanguageJourneyProgressNamespace(languageCode),
        identityScope,
        progress: normalized,
      },
    })
  );
  return normalized;
}

function updateLanguageJourneyChapter(
  languageCode: string,
  chapterId: string,
  completed: boolean,
  userId?: string | null
) {
  const journey = getLanguageJourneyByCode(languageCode);
  if (!journey?.chapters.some((chapter) => chapter.id === chapterId)) {
    return loadLanguageJourneyProgress(languageCode, userId);
  }
  const progress = loadLanguageJourneyProgress(languageCode, userId);
  const updatedAt = new Date().toISOString();
  return saveLanguageJourneyProgress(
    languageCode,
    {
      ...progress,
      updatedAt,
      lastChapterId: completed ? chapterId : progress.lastChapterId,
      chapterStates: {
        ...(progress.chapterStates || {}),
        [chapterId]: { completed, updatedAt },
      },
    },
    userId
  );
}

export function markLanguageJourneyChapterComplete(
  languageCode: string,
  chapterId: string,
  userId?: string | null
) {
  return updateLanguageJourneyChapter(languageCode, chapterId, true, userId);
}

export function markLanguageJourneyChapterIncomplete(
  languageCode: string,
  chapterId: string,
  userId?: string | null
) {
  return updateLanguageJourneyChapter(languageCode, chapterId, false, userId);
}

type LanguageJourneyProgressResponse = {
  identityId?: string;
  progress?: Partial<LanguageJourneyProgress>;
};

export async function syncLanguageJourneyProgress(languageCode: string, userId: string) {
  const local = loadLanguageJourneyProgress(languageCode, userId);
  const response = await fetch("/api/language-journey/progress", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identityId: userId, languageCode, progress: local }),
  });
  const payload = (await response
    .json()
    .catch(() => null)) as LanguageJourneyProgressResponse | null;
  if (!response.ok || !payload?.progress || payload.identityId !== userId) {
    throw new Error("Could not synchronize Language Journey progress.");
  }

  const merged = mergeLanguageJourneyProgress(languageCode, local, payload.progress);
  return saveLanguageJourneyProgress(languageCode, merged, userId);
}

export async function loadAndSyncLanguageJourneyProgress(
  languageCode: string | null | undefined,
  userId?: string | null
) {
  const local = loadLanguageJourneyProgress(languageCode, userId);
  if (!languageCode || !userId) return local;
  try {
    return await syncLanguageJourneyProgress(languageCode, userId);
  } catch {
    return local;
  }
}

export function getLanguageJourneyCompletionPercent(
  journey: LanguageJourney | null | undefined,
  progress: LanguageJourneyProgress
) {
  if (!journey?.chapters.length) return 0;
  const completed = new Set(progress.completedChapterIds);
  const completedCount = journey.chapters.filter((chapter) => completed.has(chapter.id)).length;
  return Math.round((completedCount / journey.chapters.length) * 100);
}

export function getLanguageJourneyNextIncompleteChapter(
  journey: LanguageJourney | null | undefined,
  progress: LanguageJourneyProgress
) {
  if (!journey) return null;
  const completed = new Set(progress.completedChapterIds);
  return journey.chapters.find((chapter) => !completed.has(chapter.id)) || null;
}
